import { useEffect, useCallback } from 'react';
import { useCart } from '../context/CartContext';
import { useProductsContext } from '../context/ProductsContext';

/** Live stock map + cart helpers that enforce inventory limits immediately. */
export function useCartStock() {
  const cart = useCart();
  const { syncCartStock } = cart;
  const { products, getProductById } = useProductsContext();

  useEffect(() => {
    if (!products.length) return;
    const stockMap = Object.fromEntries(products.map((p) => [p.id, p.stock]));
    const priceMap = Object.fromEntries(products.map((p) => [p.id, p.price]));
    syncCartStock({ stock: stockMap, prices: priceMap });
  }, [products, syncCartStock]);

  const getStock = useCallback(
    (productId) => {
      const live = getProductById(productId)?.stock;
      if (live !== undefined && live !== null) return Number(live) || 0;
      return 0;
    },
    [getProductById]
  );

  const increaseQuantity = useCallback(
    (item) => {
      const stock = getStock(item.id);
      cart.updateQuantity(item.id, item.quantity + 1, stock);
    },
    [cart, getStock]
  );

  const decreaseQuantity = useCallback(
    (item) => {
      const stock = getStock(item.id);
      cart.updateQuantity(item.id, item.quantity - 1, stock);
    },
    [cart, getStock]
  );

  const isSoldOut = useCallback(
    (product) => {
      if (!product) return true;
      return getStock(product.id) <= 0;
    },
    [getStock]
  );

  const getCartStockIssue = useCallback(() => {
    for (const item of cart.items) {
      const stock = getStock(item.id);
      if (stock <= 0) {
        return `${item.title} is sold out. Remove it from your cart to continue.`;
      }
      if (item.quantity > stock) {
        return `Only ${stock} available for ${item.title}.`;
      }
    }
    return '';
  }, [cart.items, getStock]);

  const canProceedToCheckout =
    cart.items.length > 0 && getCartStockIssue() === '';

  const addToCart = useCallback(
    (product, quantity = 1) => {
      const liveStock = getStock(product.id);
      if (liveStock <= 0) return;
      cart.addToCart({ ...product, stock: liveStock }, quantity);
    },
    [cart, getStock]
  );

  return {
    ...cart,
    addToCart,
    getStock,
    getCartStockIssue,
    canProceedToCheckout,
    increaseQuantity,
    decreaseQuantity,
    isSoldOut,
    products,
  };
}
